import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { api, formatApiErrorDetail } from "../api";
import AdminUsersBlock from "./AdminUsersBlock";
import RoadmapAdmin from "./RoadmapAdmin";
import WaitlistTab from "./WaitlistTab";
import FlowsTab from "./FlowsTab";

const STATUSES = ["new", "reviewed", "planned", "fixed", "ignored"];

export default function Admin() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/app");
  }, [user, navigate]);

  // Auto-switch to settings tab if we just came back from System Google OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("system_google_connected") || params.has("system_google_error")) {
      setTab("settings");
    }
  }, []);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="tb-page" data-testid="admin-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <h1 className="tb-h1" data-testid="admin-heading">{t("adminConsole.title")}</h1>
          <p className="tb-muted" style={{ marginTop: 4 }}>{t("adminConsole.subtitle")} — {user.email}</p>
        </div>
      </div>
      <div className="tb-admin-tabs" data-testid="admin-tabs">
        <button className={tab === "overview" ? "on" : ""} onClick={() => setTab("overview")} data-testid="admin-tab-overview">{t("adminConsole.tabs.overview")}</button>
        <button className={tab === "users" ? "on" : ""} onClick={() => setTab("users")} data-testid="admin-tab-users">{t("adminConsole.tabs.users")}</button>
        <button className={tab === "waitlist" ? "on" : ""} onClick={() => setTab("waitlist")} data-testid="admin-tab-waitlist">{t("adminConsole.tabs.waitlist")}</button>
        <button className={tab === "feedback" ? "on" : ""} onClick={() => setTab("feedback")} data-testid="admin-tab-feedback">{t("adminConsole.tabs.feedback")}</button>
        <button className={tab === "roadmap" ? "on" : ""} onClick={() => setTab("roadmap")} data-testid="admin-tab-roadmap">{t("adminConsole.tabs.roadmap")}</button>
        <button className={tab === "settings" ? "on" : ""} onClick={() => setTab("settings")} data-testid="admin-tab-settings">{t("adminConsole.tabs.settings")}</button>
        <button className={tab === "surveys" ? "on" : ""} onClick={() => setTab("surveys")} data-testid="admin-tab-surveys">Surveys</button>
        <button className={tab === "flows" ? "on" : ""} onClick={() => setTab("flows")} data-testid="admin-tab-flows">{t("adminConsole.tabs.flows")}</button>
      </div>
      {tab === "overview" && <OverviewTab t={t} />}
      {tab === "users" && <AdminUsersBlock />}
      {tab === "waitlist" && <WaitlistTab />}
      {tab === "feedback" && <FeedbackTab t={t} lang={lang} />}
      {tab === "roadmap" && <RoadmapAdmin t={t} />}
      {tab === "settings" && <SettingsTab t={t} />}
      {tab === "surveys" && <SurveysAdminTab t={t} lang={lang} />}
      {tab === "flows" && <FlowsTab />}
    </div>
  );
}

function OverviewTab({ t }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try { const r = await api.get("/admin/stats"); setStats(r.data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!stats) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div data-testid="admin-overview">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="tb-ghost-btn" onClick={load} disabled={refreshing} data-testid="admin-refresh">
          {refreshing ? "…" : `⟳ ${t("adminConsole.refresh")}`}
        </button>
      </div>
      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="tb-admin-kpis">
        <Kpi label={t("adminConsole.kpi.totalUsers")} value={stats.users.total} testid="kpi-total-users" />
        <Kpi label={t("adminConsole.kpi.wau7")} value={stats.activity.wau_7d} sub={`${Math.round(stats.activity.stickiness * 100)}% stickiness`} testid="kpi-wau" />
        <Kpi label={t("adminConsole.kpi.totalSessions")} value={stats.sessions.total} sub={`${stats.sessions.scheduled} ${t("adminConsole.kpi.scheduled")} · ${stats.sessions.completed} ${t("adminConsole.kpi.completed")}`} testid="kpi-sessions" />
        <Kpi label={t("adminConsole.kpi.activeMandates")} value={stats.mandates.active} sub={`${stats.mandates.total} ${t("adminConsole.kpi.total")}`} testid="kpi-mandates" />
        <Kpi label={t("adminConsole.kpi.connections")} value={stats.connections.active} testid="kpi-connections" />
        <Kpi label={t("adminConsole.kpi.newFeedback")} value={stats.feedback.new} sub={`${stats.feedback.total} ${t("adminConsole.kpi.allTime")}`} testid="kpi-feedback" />
      </div>

      <div className="tb-block" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t("adminConsole.byRoleTitle")}</h3>
        <div className="tb-admin-roles">
          {Object.entries(stats.users.by_role).sort((a, b) => b[1] - a[1]).map(([role, n]) => (
            <div className="tb-admin-role-row" key={role} data-testid={`role-row-${role}`}>
              <span className="tb-admin-role-label">{role}</span>
              <span className="tb-admin-role-bar"><span style={{ width: `${(n / stats.users.total) * 100}%` }} /></span>
              <span className="tb-admin-role-n">{n}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="tb-admin-kpi" data-testid={testid}>
      <span className="tb-admin-kpi-label">{label}</span>
      <strong className="tb-admin-kpi-value">{value}</strong>
      {sub && <span className="tb-admin-kpi-sub">{sub}</span>}
    </div>
  );
}

function FeedbackTab({ t, lang }) {
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [bugOnly, setBugOnly] = useState(false);
  const [featureOnly, setFeatureOnly] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({});

  const load = async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (bugOnly && !featureOnly) params.kind = "bug";
    else if (featureOnly && !bugOnly) params.kind = "feature";
    const q = Object.keys(params).map((k) => `${k}=${encodeURIComponent(params[k])}`).join("&");
    try {
      const r = await api.get(`/admin/feedback${q ? "?" + q : ""}`);
      setItems(r.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, [statusFilter, bugOnly, featureOnly]);  // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.patch(`/admin/feedback/${id}/status`, { status });
      await load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const promote = async (id) => {
    if (!window.confirm(t("adminConsole.feedback.promoteConfirm"))) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/admin/feedback/${id}/promote`);
      await load();
      window.alert(t("adminConsole.feedback.promoteDone"));
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  if (!items) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div data-testid="admin-feedback">
      <div className="tb-admin-fb-filters">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="fb-filter-status">
          <option value="all">{t("adminConsole.feedback.allStatuses")}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`adminConsole.feedback.status.${s}`)}</option>)}
        </select>
        <label className="tb-admin-fb-check"><input type="checkbox" checked={bugOnly} onChange={(e) => setBugOnly(e.target.checked)} data-testid="fb-filter-bug" /> {t("adminConsole.feedback.bug")}</label>
        <label className="tb-admin-fb-check"><input type="checkbox" checked={featureOnly} onChange={(e) => setFeatureOnly(e.target.checked)} data-testid="fb-filter-feature" /> {t("adminConsole.feedback.feature")}</label>
      </div>
      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}
      {items.length === 0 && <p className="tb-muted" data-testid="fb-empty">{t("adminConsole.feedback.empty")}</p>}
      <div className="tb-admin-fb-list">
        {items.map((f) => <FeedbackCard key={f.id} f={f} t={t} lang={lang} busy={!!busy[f.id]} onSetStatus={(s) => setStatus(f.id, s)} onPromote={() => promote(f.id)} />)}
      </div>
    </div>
  );
}

function FeedbackCard({ f, t, lang, busy, onSetStatus, onPromote }) {
  const status = f.status || "new";
  const created = f.created_at ? new Date(f.created_at).toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { dateStyle: "medium", timeStyle: "short" }) : "";
  return (
    <div className="tb-admin-fb-card" data-testid={`fb-card-${f.id}`}>
      <div className="tb-admin-fb-head">
        <div>
          <strong data-testid={`fb-name-${f.id}`}>{f.user_name || "—"}</strong>
          <span className="tb-muted" style={{ marginLeft: 8, fontSize: "0.82rem" }}>· {f.user_email}</span>
          {f.user_role && <span className="tb-badge accent" style={{ marginLeft: 8 }}>{f.user_role}</span>}
          <span className={`tb-admin-fb-status status-${status}`} style={{ marginLeft: 8 }} data-testid={`fb-status-${f.id}`}>{t(`adminConsole.feedback.status.${status}`)}</span>
        </div>
        <div className="tb-muted" style={{ fontSize: "0.78rem" }}>{created}</div>
      </div>
      {typeof f.rating === "number" && (
        <div className="tb-admin-fb-rating" data-testid={`fb-rating-${f.id}`}>
          {"★".repeat(f.rating)}<span className="tb-muted">{"★".repeat(5 - f.rating)}</span>
        </div>
      )}
      {f.bug && (
        <div className="tb-admin-fb-row">
          <span className="tb-admin-fb-tag bug">{t("adminConsole.feedback.bug")}</span>
          <p data-testid={`fb-bug-${f.id}`}>{f.bug}</p>
        </div>
      )}
      {f.feature && (
        <div className="tb-admin-fb-row">
          <span className="tb-admin-fb-tag feature">{t("adminConsole.feedback.feature")}</span>
          <p data-testid={`fb-feature-${f.id}`}>{f.feature}</p>
        </div>
      )}
      {f.comment && (
        <div className="tb-admin-fb-row">
          <span className="tb-admin-fb-tag note">{t("adminConsole.feedback.note")}</span>
          <p data-testid={`fb-comment-${f.id}`}>{f.comment}</p>
        </div>
      )}
      {f.image && (
        <div className="tb-admin-fb-image" data-testid={`fb-image-${f.id}`}>
          <a href={f.image} target="_blank" rel="noopener noreferrer">
            <img src={f.image} alt="Feedback attachment" />
          </a>
        </div>
      )}
      <div className="tb-admin-fb-actions">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={`tb-admin-fb-action ${status === s ? "on" : ""}`}
            onClick={() => onSetStatus(s)}
            disabled={busy || status === s}
            data-testid={`fb-action-${f.id}-${s}`}
          >
            {t(`adminConsole.feedback.status.${s}`)}
          </button>
        ))}
        {(f.feature || f.bug) && (
          <button
            type="button"
            className="tb-admin-fb-action promote"
            onClick={onPromote}
            disabled={busy}
            data-testid={`fb-promote-${f.id}`}
            title={t("adminConsole.feedback.promoteHint")}
          >
            ↗ {t("adminConsole.feedback.promote")}
          </button>
        )}
      </div>
    </div>
  );
}


function SettingsTab({ t }) {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/google-system/status");
      setStatus(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e) || "Failed to load");
    }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.has("system_google_connected") || params.has("system_google_error")) {
      params.delete("system_google_connected");
      params.delete("system_google_error");
      const next = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", next);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/google-system/start");
      window.location.href = r.data.url;
    } catch (e) {
      setErr(formatApiErrorDetail(e) || "Failed to start OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(t("adminConsole.settings.confirmDisconnect"))) return;
    setBusy(true);
    try {
      await api.delete("/admin/google-system");
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e) || "Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return <div className="tb-card"><p className="tb-muted">{t("adminConsole.settings.loading")}</p></div>;
  }

  const connected = status.connected;

  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="admin-settings-tab">
      <div className="tb-card" data-testid="system-calendar-card">
        <h3 style={{ margin: "0 0 8px" }}>{t("adminConsole.settings.systemCalendar.title")}</h3>
        <p className="tb-muted" style={{ marginTop: 0 }}>
          {t("adminConsole.settings.systemCalendar.description")}
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
          <span
            data-testid="system-calendar-status"
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              background: connected ? "rgba(110,231,183,0.12)" : "rgba(255,255,255,0.06)",
              color: connected ? "#6ee7b7" : "var(--muted)",
              border: `1px solid ${connected ? "rgba(110,231,183,0.4)" : "var(--border)"}`,
            }}
          >
            {connected
              ? `✓ ${t("adminConsole.settings.systemCalendar.connected")}`
              : t("adminConsole.settings.systemCalendar.notConnected")}
          </span>
          {connected && status.email && (
            <span className="tb-muted" data-testid="system-calendar-email">
              {status.email}
            </span>
          )}
        </div>

        {connected && status.since && (
          <p className="tb-muted" style={{ marginTop: 8, fontSize: 13 }}>
            {t("adminConsole.settings.systemCalendar.since")}: {new Date(status.since).toLocaleString()}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 18 }}>
          {!connected && (
            <button
              className="tb-primary-btn"
              onClick={connect}
              disabled={busy}
              data-testid="system-calendar-connect"
            >
              {busy ? t("adminConsole.settings.systemCalendar.connecting") : t("adminConsole.settings.systemCalendar.connect")}
            </button>
          )}
          {connected && (
            <button
              className="tb-ghost-btn"
              onClick={disconnect}
              disabled={busy}
              data-testid="system-calendar-disconnect"
              style={{ color: "#ff7a7a", borderColor: "#ff7a7a55" }}
            >
              {t("adminConsole.settings.systemCalendar.disconnect")}
            </button>
          )}
        </div>

        {err && (
          <p style={{ color: "#ff7a7a", marginTop: 12, fontSize: 13 }} data-testid="system-calendar-error">
            {err}
          </p>
        )}

        <div
          style={{
            marginTop: 20,
            padding: 14,
            borderRadius: 10,
            background: "rgba(110,184,255,0.06)",
            border: "1px solid rgba(110,184,255,0.18)",
            fontSize: 13,
            lineHeight: 1.55,
          }}
        >
          <strong style={{ color: "#6eb8ff" }}>{t("adminConsole.settings.systemCalendar.howItWorks")}</strong>
          <ol style={{ margin: "8px 0 0 18px", padding: 0 }}>
            <li>{t("adminConsole.settings.systemCalendar.step1")}</li>
            <li>{t("adminConsole.settings.systemCalendar.step2")}</li>
            <li>{t("adminConsole.settings.systemCalendar.step3")}</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

function SurveysAdminTab({ t, lang }) {
  const [surveys, setSurveys] = useState([]);
  const [selected, setSelected] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [err, setErr] = useState("");
  const [backfillMsg, setBackfillMsg] = useState("");
  const [backfilling, setBackfilling] = useState(false);

  const loadSurveys = () => {
    api.get("/admin/surveys").then((r) => setSurveys(r.data)).catch((e) => setErr(formatApiErrorDetail(e) || "Failed"));
  };

  useEffect(() => { loadSurveys(); }, []);

  const runBackfill = async () => {
    if (backfilling) return;
    setBackfilling(true);
    setBackfillMsg("");
    try {
      const r = await api.post("/admin/surveys/backfill-assignments");
      const d = r.data || {};
      setBackfillMsg(`Checked ${d.users_checked} users · Created ${d.assignments_created} (entrepreneur: ${d.by_role?.entrepreneur || 0}, advisor: ${d.by_role?.advisor || 0}) · Already assigned: ${d.already_assigned}`);
      loadSurveys();
    } catch (e) {
      setBackfillMsg(formatApiErrorDetail(e) || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const openAnalytics = async (slug) => {
    setSelected(slug);
    setAnalytics(null);
    try {
      const r = await api.get(`/admin/surveys/${slug}/analytics`);
      setAnalytics(r.data);
    } catch (e) { setErr(formatApiErrorDetail(e) || "Failed"); }
  };

  const pick = (o) => (lang === "fr" && o.label_fr) ? o.label_fr : (o.label_en || o.label_fr);

  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="admin-surveys-tab">
      {err && <p style={{ color: "#ff7a7a" }}>{err}</p>}
      <div className="tb-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0 }}>Surveys</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {backfillMsg && <span className="tb-muted" style={{ fontSize: 13 }} data-testid="backfill-msg">{backfillMsg}</span>}
            <button
              className="tb-ghost-btn"
              onClick={runBackfill}
              disabled={backfilling}
              data-testid="backfill-survey-assignments-btn"
              title="Assign persona surveys to existing entrepreneurs and advisors who registered before the survey module"
            >
              {backfilling ? "Backfilling…" : "Backfill assignments"}
            </button>
          </div>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
              <th style={{ padding: "10px 12px 10px 6px" }}>Slug</th>
              <th style={{ padding: "10px 12px" }}>Type</th>
              <th style={{ padding: "10px 12px" }}>Personas</th>
              <th style={{ textAlign: "right", padding: "10px 12px" }}>Assigned</th>
              <th style={{ textAlign: "right", padding: "10px 12px" }}>Started</th>
              <th style={{ textAlign: "right", padding: "10px 12px" }}>Completed</th>
              <th style={{ textAlign: "right", padding: "10px 16px 10px 12px" }}>Rate</th>
              <th style={{ padding: "10px 6px 10px 8px" }}></th>
            </tr>
          </thead>
          <tbody>
            {surveys.map((s) => (
              <tr key={s.slug} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`survey-row-${s.slug}`}>
                <td style={{ padding: "10px 12px 10px 6px" }}><strong>{s.slug}</strong></td>
                <td style={{ padding: "10px 12px" }}>{s.type}</td>
                <td style={{ padding: "10px 12px" }}>{(s.target_personas || []).join(", ")}</td>
                <td style={{ textAlign: "right", padding: "10px 12px" }}>{s.assigned}</td>
                <td style={{ textAlign: "right", padding: "10px 12px" }}>{s.started}</td>
                <td style={{ textAlign: "right", padding: "10px 12px" }}>{s.completed}</td>
                <td style={{ textAlign: "right", padding: "10px 16px 10px 12px" }}>{(s.completion_rate || 0).toFixed(0)}%</td>
                <td style={{ padding: "10px 6px 10px 8px", textAlign: "right" }}>
                  <button className="tb-ghost-btn" onClick={() => openAnalytics(s.slug)} data-testid={`analytics-${s.slug}`}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && analytics && (
        <div className="tb-card" style={{ padding: 28 }} data-testid="survey-analytics">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 20 }}>{lang === "fr" ? analytics.survey.title_fr : analytics.survey.title_en}</h3>
            <button className="tb-ghost-btn" onClick={() => { setSelected(null); setAnalytics(null); }} style={{ padding: "4px 12px" }}>×</button>
          </div>
          <p className="tb-muted" style={{ marginTop: 0, marginBottom: 28 }}>{analytics.total_responses} responses</p>
          <div style={{ display: "grid", gap: 22 }}>
            {analytics.question_stats.map((qs, qi) => {
              const optMap = Object.fromEntries((qs.options || []).map((o) => [o.value, pick(o)]));
              const total = Object.values(qs.counts).reduce((a, b) => a + b, 0);
              const sorted = Object.entries(qs.counts).sort((a, b) => b[1] - a[1]);
              return (
                <div key={qs.id} style={{ borderTop: qi === 0 ? "none" : "1px solid var(--border)", paddingTop: qi === 0 ? 0 : 18 }}>
                  <div style={{ marginBottom: 12 }}>
                    <strong style={{ fontSize: 15, lineHeight: 1.4 }}>{lang === "fr" ? qs.label_fr : qs.label_en}</strong>
                    <div className="tb-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 4 }}>
                      {qs.type} · {qs.type === "text" ? `${qs.open_answers.length} answers` : `${total} answers`}
                    </div>
                  </div>
                  {qs.type === "text" ? (
                    qs.open_answers.length === 0 ? (
                      <p className="tb-muted" style={{ fontSize: 13, fontStyle: "italic" }}>No open answers yet.</p>
                    ) : (
                      <div style={{ display: "grid", gap: 8 }}>
                        {qs.open_answers.slice(0, 20).map((a, i) => (
                          <div key={i} style={{ padding: "8px 12px", background: "rgba(255,255,255,0.03)", borderLeft: "3px solid #6eb8ff", fontSize: 14, lineHeight: 1.5 }}>{a}</div>
                        ))}
                      </div>
                    )
                  ) : sorted.length === 0 ? (
                    <p className="tb-muted" style={{ fontSize: 13, fontStyle: "italic" }}>No data yet.</p>
                  ) : (
                    <div style={{ display: "grid", gap: 6 }}>
                      {sorted.map(([val, n]) => {
                        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
                        const label = optMap[val] || val;
                        return (
                          <div key={val} style={{ fontSize: 13 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                              <span>{label}</span>
                              <span><strong>{n}</strong> <span className="tb-muted">({pct}%)</span></span>
                            </div>
                            <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #6eb8ff, #6ee7b7)" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

